import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useSnackbar } from '@/components/ui/magic-snackbar';
import { OnboardingForm } from './onboarding-form';

/**
 * 「정체성 정하기」 **슬라이드 창.**
 *
 * 페이지 안에 다섯 걸음을 섞어 넣으면 지금 뭘 하는 중인지 흐려진다 — 아티클을 읽다 열면 아티클
 * 화면과 질문이 한 덩어리로 보였다. 그래서 오른쪽에서 밀려 나오는 별도 창(모바일은 전체 폭)에
 * 넣는다. 오른쪽 슬라이드는 머리(z-500)와 z-index 가 엉켜 아래에서 위로 올라오는 전체 폭
 * bottom sheet 로 바꿨다. 뒤의 페이지는 그대로 있고, 끝나면 창이 닫히며 스낵바가 알린다.
 *
 * 어디서든 `useIdentitySheet().open()` 으로 연다 — 머리 아래 한 줄, 프로필의 링크, /onboarding.
 */
const Ctx = createContext<{ open: () => void; close: () => void } | null>(null);

export function useIdentitySheet() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useIdentitySheet must be used within <IdentitySheetProvider>');
  return ctx;
}

export function IdentitySheetProvider({ children }: { children: ReactNode }) {
  const [isOpen, setOpen] = useState(false);
  const { showSnackbar } = useSnackbar();
  const open = useCallback(() => setOpen(true), []);
  const close = useCallback(() => setOpen(false), []);
  const value = useMemo(() => ({ open, close }), [open, close]);

  return (
    <Ctx.Provider value={value}>
      {children}
      <Sheet open={isOpen} onOpenChange={setOpen}>
        {/* 아래에서 위로 올라오는 전체 폭 bottom sheet. 화면의 92% 높이, 위 모서리만 둥글게. */}
        <SheetContent side="bottom" className="h-[92dvh] w-full overflow-y-auto rounded-t-2xl">
          <SheetHeader className="mx-auto w-full max-w-md text-left">
            <SheetTitle>정체성 정하기</SheetTitle>
            <SheetDescription>나를 설명하는 가장 적절한 identity 를 선택하세요.</SheetDescription>
          </SheetHeader>
          <div className="mx-auto mt-4 max-w-md">
            {/* key 로 열 때마다 처음부터 — 닫았다 다시 열면 이전 답이 남지 않는다. */}
            {isOpen ? (
              <OnboardingForm
                key="identity-sheet"
                onDone={() => {
                  setOpen(false);
                  showSnackbar('정체성을 정했어요.', { type: 'success', action: { label: '마이페이지 이동', href: '/mypage' } });
                }}
              />
            ) : null}
          </div>
        </SheetContent>
      </Sheet>
    </Ctx.Provider>
  );
}
